<template>
    <div class="overflow-hidden rounded-card border border-panel-border bg-white">
        <div class="bg-panel px-4 py-1.5">
            <h4 class="!mb-0 text-xs !font-bold">
                {{ trans('workorder.case_category.title') }}
                <em v-if="!readonly" class="text-danger">*</em>
            </h4>
        </div>

        <div class="px-4 py-3">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
                <jet-select
                    v-model="categoryModel"
                    :add-toggler-classes="'w-full !rounded-lg border border-panel-border bg-white px-3 py-1.5'"
                    :disabled="effectiveCategoryReadonly"
                    :horizontal="false"
                    :inline="true"
                    :options="categoryOptions"
                    :placeholder="trans('workorder.case_category.category.placeholder')"
                    :readonly="effectiveCategoryReadonly"
                    :resettable="false"
                    :wrap-options="true"
                    class="!mb-0 min-w-0 flex-1"
                    name="case_category"
                />
                <jet-select
                    v-model="subcategoryModel"
                    :add-toggler-classes="'w-full !rounded-lg border border-panel-border bg-white px-3 py-1.5'"
                    :disabled="readonly || !subcategoryOptions.length"
                    :horizontal="false"
                    :inline="true"
                    :options="subcategoryOptions"
                    :placeholder="trans('workorder.case_category.subcategory.placeholder')"
                    :readonly="readonly"
                    :resettable="false"
                    :wrap-options="true"
                    class="!mb-0 min-w-0 flex-1"
                    name="case_subcategory"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, watch } from 'vue';
import JetSelect from '@/Jetstream/Select.vue';
import { trans } from '@/mixins/i18n.js';

const props = defineProps({
    category: { type: [String, null], default: null },
    subcategory: { type: [String, null], default: null },
    categories: { type: Array, default: () => [] },
    assetType: { type: [String, null], default: null },
    readonly: { type: Boolean, default: false },
    categoryReadonly: { type: [Boolean, null], default: null },
});

const effectiveCategoryReadonly = computed(() => props.categoryReadonly ?? props.readonly);

const emit = defineEmits(['update:category', 'update:subcategory']);

const currentCategory = computed(() => props.categories.find((c) => c.key === props.category) ?? null);

const categoryOptions = computed(() =>
    props.categories.map((c) => ({ label: c.label, value: c.key })).sort((a, b) => a.label.localeCompare(b.label)),
);

const isAllowedForAsset = (sub) => !sub.asset_types?.length || sub.asset_types.includes(props.assetType);

const subcategoryOptions = computed(() =>
    (currentCategory.value?.subcategories ?? [])
        .filter(isAllowedForAsset)
        .map((s) => ({ label: s.label, value: s.key }))
        .sort((a, b) => a.label.localeCompare(b.label)),
);

// Reset stale selection if it's no longer in the filtered list (e.g. asset type swap or category change).
watch(subcategoryOptions, (options) => {
    if (props.subcategory && !options.some((o) => o.value === props.subcategory)) {
        emit('update:subcategory', null);
    }
});

const categoryModel = computed({
    get: () => props.category,
    set: (val) => emit('update:category', val),
});

const subcategoryModel = computed({
    get: () => props.subcategory,
    set: (val) => emit('update:subcategory', val),
});
</script>
